import { create } from 'zustand';

/**
 * Workspace list + the current workspace, plus workspace-scoped flags. This is
 * the most cross-cutting state in the app (currentWorkspace is read by nearly
 * every handler), so it lives in a store that any hook/component can read via
 * getState() — removing it from a hundred useCallback dependency arrays over time.
 *
 * There was no currentWorkspaceRef mirror to delete; the gating refs
 * (prevWorkspaceIdForTabsRef, workspaceLoadKeyRef, etc.) are prev-id trackers,
 * not mirrors, and remain in App.
 *
 * Setters are exported as stable module-level functions (named identically to
 * the old useState setters); they mirror useState semantics (value or updater).
 */
const initialState = {
  workspaces: [],
  currentWorkspace: null,
  workspaceReloadNonce: 0,
  isLoadingWorkspaces: false,
  deviceLocalCollections: [],
  canPublishToNetworkPlan: false,
  gitAccount: { connected: false, login: '', avatarUrl: '' },
};

const make = (set, key) => (value) =>
  set((state) => ({ [key]: typeof value === 'function' ? value(state[key]) : value }));

export const useWorkspaceStore = create((set) => ({
  ...initialState,
  setWorkspaces: make(set, 'workspaces'),
  setCurrentWorkspace: make(set, 'currentWorkspace'),
  setWorkspaceReloadNonce: make(set, 'workspaceReloadNonce'),
  setIsLoadingWorkspaces: make(set, 'isLoadingWorkspaces'),
  setDeviceLocalCollections: make(set, 'deviceLocalCollections'),
  setCanPublishToNetworkPlan: make(set, 'canPublishToNetworkPlan'),
  setGitAccount: make(set, 'gitAccount'),
  reset: () => set({ ...initialState }),
}));

export const setWorkspaces = (v) => useWorkspaceStore.getState().setWorkspaces(v);
export const setCurrentWorkspace = (v) => useWorkspaceStore.getState().setCurrentWorkspace(v);
export const setWorkspaceReloadNonce = (v) => useWorkspaceStore.getState().setWorkspaceReloadNonce(v);
export const setIsLoadingWorkspaces = (v) => useWorkspaceStore.getState().setIsLoadingWorkspaces(v);
export const setDeviceLocalCollections = (v) => useWorkspaceStore.getState().setDeviceLocalCollections(v);
export const setCanPublishToNetworkPlan = (v) => useWorkspaceStore.getState().setCanPublishToNetworkPlan(v);
export const setGitAccount = (v) => useWorkspaceStore.getState().setGitAccount(v);
